/**
 * QuantityModal — Prompt for quantity before adding to cart.
 * Used for products with pos_requires_quantity = true.
 */
import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog, DialogContent, Box, Typography, TextField, Button,
  IconButton, InputAdornment, alpha,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

interface QuantityModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (qty: number) => void;
  productName: string;
  price: number;
}

const QuantityModal: React.FC<QuantityModalProps> = ({
  open, onClose, onConfirm, productName, price,
}) => {
  const [qty, setQty] = useState("1");
  const [error, setError] = useState("");

  // Coerce price to number (API may return string)
  const safePrice = Number(price) || 0;

  useEffect(() => {
    if (open) { setQty("1"); setError(""); }
  }, [open]);

  const numQty = parseInt(qty, 10) || 0;
  const isValid = numQty >= 1;
  const subtotal = numQty * safePrice;

  const handleConfirm = useCallback(() => {
    if (numQty < 1) { setError("Quantity must be at least 1"); return; }
    onConfirm(numQty);
  }, [numQty, onConfirm]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && isValid) handleConfirm();
    if (e.key === "Escape") onClose();
  };

  const quickQtys = [1, 2, 5, 10, 12, 24];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>

      {/* Header */}
      <Box sx={{
        background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
        px: 3, py: 2.5, display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center",
            bgcolor: "rgba(255,255,255,0.1)",
          }}>
            <AddShoppingCartIcon sx={{ fontSize: 20, color: "#fff" }} />
          </Box>
          <Box>
            <Typography fontSize={15} fontWeight={700} color="#fff">Enter Quantity</Typography>
            <Typography fontSize={11} color="rgba(255,255,255,0.5)">{productName}</Typography>
          </Box>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: "rgba(255,255,255,0.5)", "&:hover": { color: "#fff" } }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        {/* Price info */}
        <Box sx={{
          display: "flex", alignItems: "center", justifyContent: "center", gap: 1,
          p: 1.5, mb: 2.5, borderRadius: 2, bgcolor: alpha("#8b5cf6", 0.06), border: `1px solid ${alpha("#8b5cf6", 0.12)}`,
        }}>
          <Typography fontSize={13} color="#8b5cf6" fontWeight={600}>
            ${safePrice.toFixed(2)} each
          </Typography>
        </Box>

        {/* Quick quantity buttons */}
        <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
          {quickQtys.map(q => (
            <Button key={q} size="small" variant={numQty === q ? "contained" : "outlined"}
              onClick={() => { setQty(String(q)); setError(""); }}
              sx={{
                minWidth: 44, borderRadius: 2, textTransform: "none", fontSize: 13, fontWeight: 600,
                ...(numQty === q
                  ? { bgcolor: "#0f172a", "&:hover": { bgcolor: "#1e293b" } }
                  : { borderColor: "#e2e8f0", color: "#475569" }),
              }}>
              {q}
            </Button>
          ))}
        </Box>

        {/* Quantity input */}
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#475569", mb: 0.75, textTransform: "uppercase", letterSpacing: "0.02em" }}>
          Quantity
        </Typography>
        <TextField
          fullWidth autoFocus type="number" placeholder="1"
          value={qty}
          onChange={(e) => { setQty(e.target.value); setError(""); }}
          onKeyDown={handleKeyDown}
          error={!!error}
          helperText={error}
          inputProps={{ min: "1", step: "1" }}
          InputProps={{
            sx: { fontSize: 28, fontWeight: 700, fontFamily: "monospace", borderRadius: 2 },
            endAdornment: (
              <InputAdornment position="end">
                <Typography fontSize={14} fontWeight={600} color="#94a3b8">pcs</Typography>
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              "& fieldset": { borderColor: "#e2e8f0" },
              "&.Mui-focused fieldset": { borderColor: "#0f172a", borderWidth: 2 },
            },
          }}
        />

        {/* Total */}
        <Box sx={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          mt: 2.5, p: 2, borderRadius: 2,
          bgcolor: isValid ? alpha("#059669", 0.04) : "#f8fafc",
          border: `1px solid ${isValid ? alpha("#059669", 0.15) : "#e2e8f0"}`,
        }}>
          <Typography fontSize={11} color="#94a3b8" fontWeight={500}>Total</Typography>
          <Typography fontSize={22} fontWeight={800} color={isValid ? "#059669" : "#cbd5e1"} sx={{ fontFamily: "monospace" }}>
            ${isValid ? subtotal.toFixed(2) : "0.00"}
          </Typography>
        </Box>

        {/* Actions */}
        <Box sx={{ display: "flex", gap: 1.5, mt: 3 }}>
          <Button fullWidth variant="outlined" onClick={onClose}
            sx={{ borderRadius: 2, textTransform: "none", fontSize: 13, fontWeight: 600, py: 1.25, borderColor: "#e2e8f0", color: "#64748b" }}>
            Cancel
          </Button>
          <Button fullWidth variant="contained" disableElevation disabled={!isValid} onClick={handleConfirm}
            startIcon={<CheckCircleIcon sx={{ fontSize: 18 }} />}
            sx={{
              borderRadius: 2, textTransform: "none", fontSize: 13, fontWeight: 700, py: 1.25,
              bgcolor: "#0f172a", "&:hover": { bgcolor: "#1e293b" },
              "&.Mui-disabled": { bgcolor: "#e2e8f0", color: "#94a3b8" },
            }}>
            Add to Cart
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default QuantityModal;
